import { useState, useEffect } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import { adminAPI } from '../../utils/api';

const fmt = (secs) => {
  if (!secs) return '0m';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const AdminWeeklyReportPage = () => {
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => { loadSchools(); }, []);

  const loadSchools = async () => {
    try {
      const res = await adminAPI.getSchools();
      setSchools(res.data.data?.filter(s => s.status === 'approved') || []);
    } catch(e) {}
  };

  const loadReport = async (school) => {
    setSelectedSchool(school);
    setLoading(true);
    try {
      const res = await adminAPI.getWeeklyReport(school.id);
      setReport(res.data.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const sendReminders = async () => {
    if (!report) return;
    setSending(true);
    try {
      const inactiveStudents = report.students.filter(s => s.active_days === 0);
      // Get their IDs - need another API call
      const studentsRes = await adminAPI.getSchoolAnalytics(selectedSchool.id);
      const allStudents = studentsRes.data.data?.students || [];
      const inactiveIds = allStudents.filter(s => {
        const found = inactiveStudents.find(is => is.name === s.name);
        return found;
      }).map(s => s.id);

      if (inactiveIds.length === 0) { alert('No inactive students to remind.'); setSending(false); return; }

      await adminAPI.sendNotification({
        user_id: inactiveIds,
        type: 'inactivity',
        title: 'Aapka Guruji miss kar raha hai! 📚',
        message: 'Is week aapne study nahi ki. Apna streak wapas banao — aaj ek topic padho!'
      });
      alert(`Reminder sent to ${inactiveIds.length} inactive students!`);
    } catch(e) { alert('Failed to send reminders.'); }
    finally { setSending(false); }
  };

  const printReport = () => { window.print(); };

  return (
    <AdminShell title="Weekly Reports" subtitle="Generate and send weekly school performance reports">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

        {/* School selector */}
        <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px' }}>
          <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '12px' }}>Select School</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {schools.map(s => (
              <button key={s.id} onClick={() => loadReport(s)}
                style={{ padding: '8px 16px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, border: selectedSchool?.id === s.id ? 'none' : '1px solid #E2E8F0', background: selectedSchool?.id === s.id ? '#6B4EFF' : 'white', color: selectedSchool?.id === s.id ? 'white' : '#64748B', cursor: 'pointer' }}>
                {s.name}
              </button>
            ))}
          </div>
        </div>

        {loading && <div style={{ textAlign: 'center', padding: '40px', color: '#64748B' }}>Generating report...</div>}

        {report && !loading && (
          <>
            {/* Report actions */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={sendReminders} disabled={sending}
                style={{ background: '#FF6B00', color: 'white', border: 'none', borderRadius: '8px', padding: '9px 16px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', opacity: sending ? 0.7 : 1 }}>
                {sending ? 'Sending...' : '📲 Send Reminder to Inactive Students'}
              </button>
              <button onClick={printReport}
                style={{ background: '#1A3C6E', color: 'white', border: 'none', borderRadius: '8px', padding: '9px 16px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>
                🖨️ Print / Save PDF
              </button>
            </div>

            {/* Report content */}
            <div id="weekly-report" style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '32px' }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', paddingBottom: '20px', borderBottom: '2px solid #6B4EFF' }}>
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#6B4EFF', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Weekly Performance Report</div>
                  <div style={{ fontSize: '22px', fontWeight: 900, color: '#1A1A2E' }}>{report.school?.name}</div>
                  <div style={{ fontSize: '12px', color: '#64748B', marginTop: '2px' }}>{report.school?.city} · Principal: {report.school?.principal_name}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '11px', color: '#64748B' }}>Report generated</div>
                  <div style={{ fontSize: '13px', fontWeight: 700 }}>{report.week}</div>
                  <div style={{ fontSize: '11px', color: '#64748B', marginTop: '2px' }}>MyPersonalGuruji.com</div>
                </div>
              </div>

              {/* Summary cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', marginBottom: '24px' }}>
                {[
                  { label: 'Total Students', val: report.summary.total_students, color: '#6B4EFF', bg: '#F3F0FF' },
                  { label: 'Active This Week', val: report.summary.active_students, color: '#1B7F3E', bg: '#E6F4EC' },
                  { label: 'Study Hours', val: report.summary.total_study_hours + 'h', color: '#1A3C6E', bg: '#E8F0FB' },
                  { label: 'Topics Covered', val: report.summary.total_topics, color: '#B45309', bg: '#FEF3C7' },
                  { label: 'Avg Quiz Score', val: report.summary.avg_score + '%', color: '#DC2626', bg: '#FEE2E2' },
                ].map(s => (
                  <div key={s.label} style={{ background: s.bg, borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
                    <div style={{ fontSize: '22px', fontWeight: 900, color: s.color }}>{s.val}</div>
                    <div style={{ fontSize: '10px', color: '#64748B', fontWeight: 600, marginTop: '3px' }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Student table */}
              <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '12px' }}>Student-wise Performance</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: '#F7F8FC', borderBottom: '2px solid #E2E8F0' }}>
                    {['#', 'Student Name', 'Class', 'Study Time', 'Topics', 'Quizzes', 'Avg Score', 'Active Days', 'Status'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '10px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.students.map((s, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #F1F5F9', background: s.active_days === 0 ? '#FFFBEB' : 'white' }}>
                      <td style={{ padding: '10px 12px', color: '#94A3B8', fontWeight: 700 }}>{i + 1}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 700 }}>{s.name}</td>
                      <td style={{ padding: '10px 12px' }}>Class {s.class_id}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 700, color: s.week_seconds > 0 ? '#1A3C6E' : '#94A3B8' }}>{fmt(s.week_seconds)}</td>
                      <td style={{ padding: '10px 12px' }}>{s.week_topics}</td>
                      <td style={{ padding: '10px 12px' }}>{s.week_quizzes}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 700, color: s.avg_score >= 70 ? '#1B7F3E' : s.avg_score >= 40 ? '#B45309' : '#94A3B8' }}>
                        {s.avg_score > 0 ? Math.round(s.avg_score) + '%' : '—'}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', gap: '3px' }}>
                          {[0,1,2,3,4,5,6].map(d => (
                            <div key={d} style={{ width: '10px', height: '10px', borderRadius: '2px', background: d < s.active_days ? '#6B4EFF' : '#E2E8F0' }}></div>
                          ))}
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '20px', background: s.active_days >= 5 ? '#E6F4EC' : s.active_days >= 3 ? '#FEF3C7' : '#FEE2E2', color: s.active_days >= 5 ? '#1B7F3E' : s.active_days >= 3 ? '#B45309' : '#DC2626' }}>
                          {s.active_days >= 5 ? 'Excellent' : s.active_days >= 3 ? 'Good' : s.active_days > 0 ? 'Low' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #E2E8F0', fontSize: '11px', color: '#94A3B8', textAlign: 'center' }}>
                Generated by MyPersonalGuruji · mypersonalguruji.com · Confidential — For School Use Only
              </div>
            </div>
          </>
        )}
      </div>
    </AdminShell>
  );
};

export default AdminWeeklyReportPage;
